import { __read, __values, __assign, __awaiter, __generator, __extends, __decorate, __metadata } from 'tslib';
import { NgZone, defineInjectable, inject, Injectable, EventEmitter, ElementRef, Renderer2, Output, Input, HostListener, Directive, HostBinding, ContentChild, NgModule } from '@angular/core';
import { Subject } from 'rxjs';
import { startWith, map } from 'rxjs/operators';

/**
 * @internal
 */
var ErrorType;
(function (ErrorType) {
    ErrorType[ErrorType["Restart"] = 0] = "Restart";
    ErrorType[ErrorType["Auth"] = 1] = "Auth";
    ErrorType[ErrorType["Retryable"] = 2] = "Retryable";
    ErrorType[ErrorType["FatalError"] = 3] = "FatalError";
})(ErrorType || (ErrorType = {}));
var ErrorHandler = /** @class */ (function () {
    function ErrorHandler() {
        this.min = 500;
        this.max = this.min * 120;
        this.factor = 2;
        this.code = -1;
        this.attempts = 1;
        this.delay = this.min;
    }
    ErrorHandler.prototype.kind = function (code) {
        if (code === this.code) {
            this.attempts++;
            if (this.attempts > ErrorHandler.maxRetryAttempts) {
                return ErrorType.FatalError;
            }
        }
        else {
            this.reset();
        }
        this.code = code;
        if (ErrorHandler.authErrorCodes.includes(code)) {
            return ErrorType.Auth;
        }
        if (ErrorHandler.shouldRestartCodes.includes(code)) {
            return ErrorType.Restart;
        }
        if (code < 400 || code >= 500 || ErrorHandler.shouldRetryCodes.includes(code)) {
            return ErrorType.Retryable;
        }
        return ErrorType.FatalError;
    };
    ErrorHandler.prototype.wait = function () {
        var _this = this;
        return new Promise(function (resolve) {
            _this.delay = Math.min(_this.delay * _this.factor, _this.max);
            setTimeout(function () { return resolve(_this.attempts); }, _this.delay + Math.floor(Math.random() * _this.min));
        });
    };
    ErrorHandler.prototype.reset = function () {
        this.delay = this.min;
        this.attempts = 1;
        this.code = -1;
    };
    ErrorHandler.maxRetryAttempts = 8;
    ErrorHandler.shouldRestartCodes = [404, 410];
    ErrorHandler.authErrorCodes = [401];
    ErrorHandler.shouldRetryCodes = [423, 429];
    return ErrorHandler;
}());

var PREFIX = 'UPLOADX-V3.0-';
var Store = /** @class */ (function () {
    function Store(prefix) {
        if (prefix === void 0) { prefix = ''; }
        this.prefix = prefix;
    }
    Store.prototype.set = function (key, value) {
        localStorage.setItem(this.prefix + key, value);
    };
    Store.prototype.get = function (key) {
        return localStorage.getItem(this.prefix + key);
    };
    Store.prototype.delete = function (key) {
        localStorage.removeItem(this.prefix + key);
    };
    return Store;
}());
var store = 'localStorage' in window ? new Store(PREFIX) : new Map();

function safeMatch(base, re) {
    return (base.match(re) || [])[0] || '';
}
function resolveUrl(url, base) {
    if (url.indexOf('https://') * url.indexOf('http://') === 0) {
        return url;
    }
    if (url.indexOf('//') === 0) {
        return safeMatch(base, /^(https?:)/) + url;
    }
    if (url.indexOf('/') === 0) {
        return safeMatch(base, /^(?:https?:)?(?:\/\/)?([^\/\?]+)/) + url;
    }
    return safeMatch(base, /^(?:https?:)?(?:\/\/)?([^\/\?]+)?(.*\/)/) + url;
}
function unfunc(value, ref) {
    return value instanceof Function ? value(ref) : value;
}
var noop = function () { };
var pick = function (obj, whitelist) {
    var result = {};
    whitelist.forEach(function (key) { return (result[key] = obj[key]); });
    return result;
};
function isNumber(x) {
    return x === Number(x);
}
function isString(x) {
    return typeof x === 'string';
}
var actionToStatusMap = {
    pause: 'paused',
    pauseAll: 'paused',
    upload: 'queue',
    uploadAll: 'queue',
    cancel: 'cancelled',
    cancelAll: 'cancelled'
};
/**
 * 32-bit FNV-1a hash function
 */
function createHash(str) {
    var hash = 2166136261;
    var len = str.length;
    for (var i = 0; i < len; i++) {
        hash ^= str.charCodeAt(i);
        hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
    }
    return hash >>> 0;
}
var b64 = {
    encode: function (str) {
        return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, function (_match, p1) {
            return String.fromCharCode(Number.parseInt(p1, 16));
        }));
    },
    decode: function (str) {
        return decodeURIComponent(atob(str)
            .split('')
            .map(function (c) { return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2); })
            .join(''));
    },
    serialize: function (obj) {
        return Object.entries(obj)
            .map(function (_a) {
            var _b = __read(_a, 2), key = _b[0], value = _b[1];
            return key + " " + b64.encode(String(value));
        })
            .toString();
    },
    parse: function (encoded) {
        var e_1, _a;
        var kvPairs = encoded.split(',').map(function (kv) { return kv.split(' '); });
        var decoded = Object.create(null);
        try {
            for (var kvPairs_1 = __values(kvPairs), kvPairs_1_1 = kvPairs_1.next(); !kvPairs_1_1.done; kvPairs_1_1 = kvPairs_1.next()) {
                var _b = __read(kvPairs_1_1.value, 2), key = _b[0], value = _b[1];
                decoded[key] = b64.decode(value);
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (kvPairs_1_1 && !kvPairs_1_1.done && (_a = kvPairs_1.return)) _a.call(kvPairs_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        return decoded;
    }
};

/**
 * Uploader Base Class
 */
var Uploader = /** @class */ (function () {
    function Uploader(file, options) {
        var _this = this;
        this.file = file;
        this.options = options;
        this._url = '';
        /** Custom headers */
        this.headers = {};
        /** Upload endpoint */
        this.endpoint = '/upload';
        this.partNumber = 1;
        this.key = null;
        this.uploadIdS3 = null;
        this.encryptedSize = 0;
        /**
         * Retries handler
         */
        this.errorHandler = new ErrorHandler();
        /**
         * byte offset within the whole file
         */
        this.offset = 0;
        /**
         * Set HttpRequest responseType
         */
        this.responseType = '';
        this.cleanup = function () { return store.delete(_this.uploadId); };
        this.name = file.name;
        this.size = file.size;
        this.metadata = {
            name: file.name,
            mimeType: file.type,
            size: file.size,
            lastModified: file.lastModified
        };
        var print = JSON.stringify(__assign({}, this.metadata, { type: this.constructor.name, endpoint: options.endpoint, fileParams: options.fileParams, encryptionHandler: options.encryptionHandler, securityHandler: options.securityHandler }));
        this.uploadId = createHash(print).toString(16);
        this.stateChange = options.stateChange || noop;
        this.chunkSize = options.chunkSize || Uploader.startingChunkSize;
        this.configure(options);
    }
    Object.defineProperty(Uploader.prototype, "status", {
        get: function () {
            return this._status;
        },
        set: function (s) {
            if (this._status === 'cancelled' || (this._status === 'complete' && s !== 'cancelled')) {
                return;
            }
            if (s !== this._status) {
                s === 'paused' && this.abort();
                s === 'cancelled' && this.onCancel();
                ['cancelled', 'complete', 'error'].includes(s) && this.cleanup();
                this._status = s;
                this.stateChange(this);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Uploader.prototype, "url", {
        get: function () {
            return this._url || store.get(this.uploadId) || '';
        },
        set: function (value) {
            this._url !== value && store.set(this.uploadId, value);
            this._url = value;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Configure or reconfigure uploader
     */
    Uploader.prototype.configure = function (_a) {
        var _b = _a.metadata, metadata = _b === void 0 ? {} : _b, _c = _a.headers, headers = _c === void 0 ? {} : _c, token = _a.token, endpoint = _a.endpoint, fileParams = _a.fileParams, encryptionHandler = _a.encryptionHandler, securityHandler = _a.securityHandler, action = _a.action;
        this.endpoint = endpoint || this.endpoint;
        this.token = token || this.token;
        this.metadata = __assign({}, this.metadata, unfunc(metadata, this.file));
        this.headers = __assign({}, this.headers, unfunc(headers, this.file));
        this.fileParams = fileParams || this.fileParams;
        this.encryptionHandler = encryptionHandler || this.encryptionHandler;
        this.securityHandler = securityHandler || this.securityHandler;
        action && (this.status = actionToStatusMap[action]);
    };
    /**
     * Initiate upload
     */
    Uploader.prototype.upload = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        this.status = 'uploading';
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 3, , 7]);
                        return [4 /*yield*/, this.getToken()];
                    case 2:
                        _b.sent();
                        if (this.encryptionHandler) {
                            this.encryptedSize = this.file.size + (16 - (this.file.size % 16));
                            this.encryptedSize += 16 * Math.ceil(this.file.size / this.chunkSize);
                            this.size = this.encryptedSize;
                        }
                        this.offset = 0;
                        this.partNumber = 1;
                        this.url = this.url;
                        this.errorHandler.reset();
                        this.startTime = new Date().getTime();
                        this.start();
                        return [3 /*break*/, 7];
                    case 3:
                        _a = _b.sent();
                        if (!(this.errorHandler.kind(this.responseStatus) !== ErrorType.FatalError)) return [3 /*break*/, 5];
                        this.status = 'retry';
                        return [4 /*yield*/, this.errorHandler.wait()];
                    case 4:
                        _b.sent();
                        this.status = 'queue';
                        return [3 /*break*/, 6];
                    case 5:
                        this.status = 'error';
                        _b.label = 6;
                    case 6: return [3 /*break*/, 7];
                    case 7: return [2 /*return*/];
                }
            });
        });
    };
    Uploader.prototype.setAuth = function (token) {
        this.headers.Authorization = "" + token;
    };
    Uploader.prototype.adjustChunkSize = function () {
        if (!this.options.chunkSize && this.responseStatus < 400) {
            var elapsedTime = this.chunkSize / this.speed;
            if (elapsedTime < 2) {
                this.chunkSize = Math.min(Uploader.maxChunkSize, this.chunkSize * 2);
            }
            if (elapsedTime > 8) {
                this.chunkSize = Math.max(Uploader.minChunkSize, this.chunkSize / 2);
            }
        }
        else if (this.responseStatus === 413) {
            this.chunkSize /= 2;
            Uploader.maxChunkSize = this.chunkSize;
        }
        Uploader.startingChunkSize = this.chunkSize;
    };
    Uploader.prototype.abort = function () {
        this.offset = undefined;
        this.partNumber = 1;
        this._xhr && this._xhr.abort();
    };
    Uploader.prototype.onCancel = function () {
        this.abort();
        this.url && this.request({ method: 'DELETE' }).catch(function (err) { });
    };
    /**
     * Gets the value from the response header
     */
    Uploader.prototype.getValueFromResponseHeader = function (key) {
        return this._xhr.getResponseHeader(key);
    };
    /**
     * Gets the value from the response body
     */
    Uploader.prototype.getValueFromResponseBody = function (key) {
        if (this._xhr.responseType == 'json') {
            return this._xhr.response[key];
        }
        else {
            return null;
        }
    };
    /**
     * Set auth token
     */
    Uploader.prototype.getToken = function () {
        var _this = this;
        return Promise.resolve(unfunc(this.token || '', this.responseStatus)).then(function (token) { return token && _this.setAuth(token); });
    };
    /**
     * Starts the actual uploading
     */
    Uploader.prototype.start = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a, key, partNumber, partSize, uploadIdS3, _b, offset, _c, errType;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        if (!(this.status === 'uploading' || this.status === 'retry')) return [3 /*break*/, 18];
                        _d.label = 1;
                    case 1:
                        _d.trys.push([1, 9, 16, 17]);
                        if (!isNumber(this.partNumber)) return [3 /*break*/, 3];
                        return [4 /*yield*/, this.sendFileContent()];
                    case 2:
                        _b = _d.sent();
                        return [3 /*break*/, 5];
                    case 3: return [4 /*yield*/, this.getPartInfo()];
                    case 4:
                        _b = _d.sent();
                        _d.label = 5;
                    case 5:
                        _a = _b, key = _a.key, partNumber = _a.partNumber, partSize = _a.partSize, uploadIdS3 = _a.uploadIdS3;
                        offset = isNumber(this.offset)
                            ? this.offset + partSize
                            : partSize;
                        this.uploadIdS3 = uploadIdS3;
                        this.key = key;
                        if (!(offset === this.size)) return [3 /*break*/, 7];
                        return [4 /*yield*/, this.completeMultipartUpload()];
                    case 6:
                        _d.sent();
                        this.offset = offset;
                        this.partNumber = partNumber;
                        this.progress = 100;
                        this.remaining = 0;
                        this.loaded = this.size;
                        this.status = 'complete';
                        return [3 /*break*/, 8];
                    case 7:
                        if (offset === this.offset) {
                            throw new Error('Content upload failed');
                        }
                        _d.label = 8;
                    case 8:
                        this.errorHandler.reset();
                        this.offset = offset;
                        partNumber += 1;
                        this.partNumber = partNumber;
                        return [3 /*break*/, 17];
                    case 9:
                        _c = _d.sent();
                        errType = this.errorHandler.kind(this.responseStatus);
                        if (!(errType === ErrorType.FatalError)) return [3 /*break*/, 10];
                        this.status = 'error';
                        return [3 /*break*/, 15];
                    case 10:
                        if (!(errType === ErrorType.Restart)) return [3 /*break*/, 11];
                        this.url = '';
                        this.status = 'queue';
                        return [3 /*break*/, 15];
                    case 11:
                        if (!(errType === ErrorType.Auth)) return [3 /*break*/, 13];
                        return [4 /*yield*/, this.getToken()];
                    case 12:
                        _d.sent();
                        return [3 /*break*/, 15];
                    case 13:
                        this.status = 'retry';
                        return [4 /*yield*/, this.errorHandler.wait()];
                    case 14:
                        _d.sent();
                        this.offset = this.responseStatus >= 400 ? undefined : this.offset;
                        this.partNumber = this.responseStatus >= 400 ? 1 : this.partNumber;
                        this.status = 'uploading';
                        _d.label = 15;
                    case 15: return [3 /*break*/, 17];
                    case 16:
                        this.adjustChunkSize();
                        return [7 /*endfinally*/];
                    case 17: return [3 /*break*/, 0];
                    case 18: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Request method
     */
    Uploader.prototype.request = function (_a) {
        var _this = this;
        var method = _a.method, _b = _a.body, body = _b === void 0 ? null : _b, url = _a.url, _c = _a.headers, headers = _c === void 0 ? {} : _c;
        return new Promise(function (resolve, reject) {
            var xhr = (_this._xhr = new XMLHttpRequest());
            xhr.open(method, url || _this.url, true);
            if (body instanceof Blob) {
                xhr.upload.onprogress = _this.onProgress(body.size);
            }
            _this.responseStatus = 0;
            _this.response = undefined;
            _this.responseType && (xhr.responseType = _this.responseType);
            _this.options.withCredentials && (xhr.withCredentials = true);
            var _headers = __assign({}, _this.headers, headers);
            Object.keys(_headers).forEach(function (key) { return xhr.setRequestHeader(key, _headers[key]); });
            xhr.onload = function (evt) {
                _this.processResponse(xhr);
                _this.responseStatus >= 400 ? reject(evt) : resolve(evt);
            };
            xhr.onerror = reject;
            xhr.send(body);
        });
    };
    Uploader.prototype.getResponseBody = function (xhr) {
        var body = 'response' in xhr ? xhr.response : xhr.responseText;
        if (body && this.responseType === 'json' && typeof body === 'string') {
            try {
                body = JSON.parse(body);
            }
            catch (_a) { }
        }
        return body;
    };
    Uploader.prototype.getChunk = function () {
        return __awaiter(this, void 0, void 0, function () {
            var start, end, body, fileName, fileType;
            return __generator(this, function (_a) {
                start = this.offset;
                end = this.chunkSize ? Math.min(start + this.chunkSize, this.size) : this.size;
                body = this.file.slice(this.offset, end);
                fileName = this.file.name;
                fileType = this.file.type;
                return [2 /*return*/, { start: start, end: end, body: body, fileName: fileName, fileType: fileType }];
            });
        });
    };
    Uploader.prototype.processResponse = function (xhr) {
        this.responseStatus = xhr.status;
        this.response = this.responseStatus !== 204 ? this.getResponseBody(xhr) : '';
    };
    Uploader.prototype.onProgress = function (chunkSize) {
        var _this = this;
        var throttle = 0;
        return function (_a) {
            var loaded = _a.loaded;
            if (!throttle) {
                throttle = window.setTimeout(function () { return (throttle = 0); }, 500);
                var now = new Date().getTime();
                var uploaded = _this.offset + chunkSize * (loaded / chunkSize);
                _this.progress = +((uploaded / _this.size) * 100).toFixed(2);
                var elapsedTime = (now - _this.startTime) / 1000;
                _this.speed = Math.round(uploaded / elapsedTime);
                _this.remaining = Math.ceil((_this.size - uploaded) / _this.speed);
                _this.loaded = uploaded;
                _this.stateChange(_this);
            }
        };
    };
    /** Maximum chunk size in bytes */
    Uploader.maxChunkSize = Number.MAX_SAFE_INTEGER;
    /** Minimum chunk size in bytes */
    Uploader.minChunkSize = 4096; // default blocksize of most FSs
    /** Initial chunk size in bytes */
    Uploader.startingChunkSize = 4096 * 256;
    return Uploader;
}());

var getMimeType = require('simple-mime')('application/octect-stream');
/**
 * Implements XHR/CORS Resumable Upload
 * @see
 * https://developers.google.com/drive/api/v3/manage-uploads#resumable
 */
var UploaderX = /** @class */ (function (_super) {
    __extends(UploaderX, _super);
    function UploaderX(file, options) {
        var _this = _super.call(this, file, options) || this;
        _this.file = file;
        _this.responseType = 'json';
        return _this;
    }
    UploaderX.prototype.getFileUrl = function (size) {
        return __awaiter(this, void 0, void 0, function () {
            var data, url;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        data = new FormData();
                        if (this.fileParams.parentId) {
                            data.append('parentId', this.fileParams.parentId);
                        }
                        data.append('partNumber', this.partNumber.toString());
                        data.append('partSize', size.toString());
                        if (this.uploadIdS3) {
                            data.append('uploadId', this.uploadIdS3);
                        }
                        if (this.key) {
                            data.append('key', this.key);
                        }
                        return [4 /*yield*/, this.request({
                                url: this.endpoint,
                                method: 'POST',
                                body: data
                            })];
                    case 1:
                        _a.sent();
                        url = this.getValueFromResponseBody('url');
                        if (!url) {
                            throw new Error('Invalid or missing chunk url');
                        }
                        return [2 /*return*/, this.getPartInfo()];
                }
            });
        });
    };
    UploaderX.prototype.sendFileContent = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a, start, end, body, partInfo;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, this.getChunk()];
                    case 1:
                        _a = _b.sent(), start = _a.start, end = _a.end, body = _a.body;
                        return [4 /*yield*/, this.getFileUrl(end - start)];
                    case 2:
                        partInfo = _b.sent();
                        return [4 /*yield*/, this.request({
                                method: 'PUT',
                                body: body,
                                url: partInfo.url
                            })];
                    case 3:
                        _b.sent();
                        return [2 /*return*/, partInfo];
                }
            });
        });
    };
    UploaderX.prototype.completeMultipartUpload = function () {
        return __awaiter(this, void 0, void 0, function () {
            var data, fileName, fileKey;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        data = new FormData();
                        if (this.fileParams.parentId) {
                            data.append('parentId', this.fileParams.parentId);
                        }
                        if (this.fileParams.path) {
                            data.append('path', this.fileParams.path);
                        }
                        if (this.uploadIdS3) {
                            data.append('uploadId', this.uploadIdS3);
                        }
                        if (this.key) {
                            data.append('key', this.key);
                        }
                        this.encryptionHandler.resetIV();
                        return [4 /*yield*/, this.encryptionHandler.setKey(this.fileParams.fileKey || '')];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, this.encryptionHandler.encryptBlob(new Blob([this.file.name]))];
                    case 2:
                        fileName = _a.sent();
                        data.append('filename', btoa(String.fromCharCode.apply(null, new Uint8Array(fileName))));
                        data.append('filesize', this.file.size.toString());
                        data.append('mime', getMimeType(this.file.name) || '');
                        data.append('extension', this.file.name.split('.')[this.file.name.split('.').length - 1]);
                        return [4 /*yield*/, this.securityHandler.encryptKey(this.fileParams.fileKey)];
                    case 3:
                        fileKey = _a.sent();
                        if (fileKey) {
                            data.append('fileKey', btoa(String.fromCharCode.apply(null, new Uint8Array(fileKey))));
                        }
                        return [4 /*yield*/, this.request({
                                method: 'POST',
                                body: data,
                                url: this.endpoint + '/complete'
                            })];
                    case 4:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    UploaderX.prototype.getPartInfo = function () {
        return __awaiter(this, void 0, void 0, function () {
            var url, key, partNumber, partSize, uploadIdS3;
            return __generator(this, function (_a) {
                if (this.responseStatus == 201) {
                    url = this.getValueFromResponseBody('url');
                    key = this.getValueFromResponseBody('key');
                    partNumber = this.getValueFromResponseBody('partNumber');
                    partSize = this.getValueFromResponseBody('partSize');
                    uploadIdS3 = this.getValueFromResponseBody('uploadId');
                    if (partNumber != null)
                        partNumber = parseInt(partNumber);
                    if (partSize != null)
                        partSize = parseInt(partSize);
                    return [2 /*return*/, {
                            url: url,
                            key: key,
                            partNumber: partNumber,
                            partSize: partSize,
                            uploadIdS3: uploadIdS3
                        }];
                }
                else {
                    return [2 /*return*/, {
                            url: '',
                            key: '',
                            partNumber: 0,
                            partSize: 0,
                            uploadIdS3: ''
                        }];
                }
                return [2 /*return*/];
            });
        });
    };
    return UploaderX;
}(Uploader));

var UploadxService = /** @class */ (function () {
    function UploadxService(ngZone) {
        var _this = this;
        this.ngZone = ngZone;
        this.eventsStream = new Subject();
        /**
         * Array of the upload queue
         */
        this.queue = [];
        this.options = {
            endpoint: '/upload',
            autoUpload: true,
            concurrency: 2,
            stateChange: function (evt) {
                setTimeout(function () {
                    return _this.ngZone.run(function () { return _this.eventsStream.next(pick(evt, UploadxService_1.stateKeys)); });
                });
            }
        };
        this.events.subscribe(function (_a) {
            var status = _a.status;
            if (status !== 'uploading' && status !== 'added') {
                _this.ngZone.runOutsideAngular(function () { return _this.processQueue(); });
            }
        });
    }
    UploadxService_1 = UploadxService;
    Object.defineProperty(UploadxService.prototype, "events", {
        /**
         * Upload status events
         */
        get: function () {
            return this.eventsStream.asObservable();
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Initializes service
     * @param options global module options
     * @returns Observable that emits a new value on progress or status changes
     */
    UploadxService.prototype.init = function (options) {
        if (options === void 0) { options = {}; }
        Object.assign(this.options, options);
        return this.events;
    };
    /**
     * Initializes service
     * @param options global module options
     * @returns Observable that emits the current array of uploaders
     */
    UploadxService.prototype.connect = function (options) {
        var _this = this;
        return this.init(options).pipe(startWith(0), map(function () { return _this.queue; }));
    };
    /**
     * Terminates all uploads and clears the queue
     */
    UploadxService.prototype.disconnect = function () {
        this.queue.forEach(function (uploader) { return (uploader.status = 'paused'); });
        this.queue = [];
    };
    /**
     * Create Uploader and add to the queue
     */
    UploadxService.prototype.handleFileList = function (fileList, options) {
        var _this = this;
        if (options === void 0) { options = {}; }
        var instanceOptions = __assign({}, this.options, options);
        this.options.concurrency = instanceOptions.concurrency;
        Array.from(fileList).forEach(function (file) { return _this.addUploaderInstance(file, instanceOptions); });
        this.autoUploadFiles();
    };
    /**
     * Create Uploader for the file and add to the queue
     */
    UploadxService.prototype.handleFile = function (file, options) {
        if (options === void 0) { options = {}; }
        var instanceOptions = __assign({}, this.options, options);
        this.addUploaderInstance(file, instanceOptions);
        this.autoUploadFiles();
    };
    UploadxService.prototype.addUploaderInstance = function (file, options) {
        var uploader = new (options.uploaderClass || UploaderX)(file, options);
        this.queue.push(uploader);
        uploader.status = 'added';
    };
    /**
     * Auto upload the files if the flag is true
     * @internal
     */
    UploadxService.prototype.autoUploadFiles = function () {
        if (this.options.autoUpload) {
            this.queue
                .filter(function (_a) {
                var status = _a.status;
                return status === 'added';
            })
                .forEach(function (uploader) { return (uploader.status = 'queue'); });
        }
    };
    /**
     * Upload control
     * @example
     * // pause all
     * this.uploadService.control({ action: 'pause' });
     * // pause upload with uploadId
     * this.uploadService.control({ action: 'pause', uploadId});
     * // set token
     * this.uploadService.control({ token: `TOKEN` });
     */
    UploadxService.prototype.control = function (evt) {
        var target = evt.uploadId
            ? this.queue.filter(function (_a) {
                var uploadId = _a.uploadId;
                return uploadId === evt.uploadId;
            })
            : this.queue;
        target.forEach(function (uploader) { return uploader.configure(evt); });
    };
    /**
     * Queue management
     * @internal
     */
    UploadxService.prototype.processQueue = function () {
        // Remove Cancelled Items from local queue
        this.queue = this.queue.filter(function (_a) {
            var status = _a.status;
            return status !== 'cancelled';
        });
        this.queue
            .filter(function (_a) {
            var status = _a.status;
            return status === 'queue';
        })
            .slice(0, Math.max((this.options.concurrency || 2) - this.runningProcess(), 0))
            .forEach(function (uploader) { return uploader.upload(); });
    };
    /**
     * @returns number of active uploads
     */
    UploadxService.prototype.runningProcess = function () {
        return this.queue.filter(function (_a) {
            var status = _a.status;
            return status === 'uploading' || status === 'retry';
        }).length;
    };
    var UploadxService_1;
    UploadxService.stateKeys = [
        'file',
        'name',
        'progress',
        'loaded',
        'remaining',
        'response',
        'responseStatus',
        'size',
        'speed',
        'status',
        'uploadId',
        'url'
    ];
    UploadxService.ctorParameters = function () { return [
        { type: NgZone }
    ]; };
    UploadxService.ngInjectableDef = defineInjectable({ factory: function UploadxService_Factory() { return new UploadxService(inject(NgZone)); }, token: UploadxService, providedIn: "root" });
    UploadxService = UploadxService_1 = __decorate([
        Injectable({ providedIn: 'root' }),
        __metadata("design:paramtypes", [NgZone])
    ], UploadxService);
    return UploadxService;
}());

var UploadxDirective = /** @class */ (function () {
    function UploadxDirective(elementRef, renderer, uploadService) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.uploadService = uploadService;
        this.uploadxState = new EventEmitter();
    }
    Object.defineProperty(UploadxDirective.prototype, "uploadxAction", {
        set: function (ctrlEvent) {
            if (ctrlEvent && this.uploadService) {
                this.uploadService.control(ctrlEvent);
            }
        },
        enumerable: true,
        configurable: true
    });
    UploadxDirective.prototype.ngOnInit = function () {
        if (!this.uploadx || this.uploadx.multiple !== false) {
            this.renderer.setAttribute(this.elementRef.nativeElement, 'multiple', '');
        }
        if (this.uploadx && this.uploadx.allowedTypes) {
            this.renderer.setAttribute(this.elementRef.nativeElement, 'accept', this.uploadx.allowedTypes);
        }
        this.uploadxState.emit(this.uploadService.events);
    };
    UploadxDirective.prototype.fileListener = function (files) {
        if (files && files.item(0)) {
            this.uploadService.handleFileList(files, this.uploadx);
        }
    };
    UploadxDirective.ctorParameters = function () { return [
        { type: ElementRef },
        { type: Renderer2 },
        { type: UploadxService }
    ]; };
    __decorate([
        Output(),
        __metadata("design:type", Object)
    ], UploadxDirective.prototype, "uploadxState", void 0);
    __decorate([
        Input(),
        __metadata("design:type", Object)
    ], UploadxDirective.prototype, "uploadx", void 0);
    __decorate([
        Input(),
        __metadata("design:type", Object),
        __metadata("design:paramtypes", [Object])
    ], UploadxDirective.prototype, "uploadxAction", null);
    __decorate([
        HostListener('change', ['$event.target.files']),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [FileList]),
        __metadata("design:returntype", void 0)
    ], UploadxDirective.prototype, "fileListener", null);
    UploadxDirective = __decorate([
        Directive({
            selector: '[uploadx]'
        }),
        __metadata("design:paramtypes", [ElementRef,
            Renderer2,
            UploadxService])
    ], UploadxDirective);
    return UploadxDirective;
}());

var UploadxDropDirective = /** @class */ (function () {
    function UploadxDropDirective(uploadService) {
        this.uploadService = uploadService;
        this.active = false;
    }
    UploadxDropDirective.prototype.dropHandler = function (event) {
        if (event.dataTransfer) {
            event.stopPropagation();
            event.preventDefault();
            event.dataTransfer.dropEffect = 'copy';
            this.active = false;
            var files = event.dataTransfer.files;
            if (files && files.item(0)) {
                this.uploadService.handleFileList(files, this.fileInput.uploadx);
            }
        }
    };
    UploadxDropDirective.prototype.onDragOver = function (event) {
        event.stopPropagation();
        event.preventDefault();
        this.active = true;
    };
    UploadxDropDirective.prototype.onDragLeave = function (event) {
        this.active = false;
    };
    UploadxDropDirective.ctorParameters = function () { return [
        { type: UploadxService }
    ]; };
    __decorate([
        HostBinding('class.uploadx-drop-active'),
        __metadata("design:type", Object)
    ], UploadxDropDirective.prototype, "active", void 0);
    __decorate([
        ContentChild(UploadxDirective),
        __metadata("design:type", UploadxDirective)
    ], UploadxDropDirective.prototype, "fileInput", void 0);
    __decorate([
        HostListener('drop', ['$event']),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [DragEvent]),
        __metadata("design:returntype", void 0)
    ], UploadxDropDirective.prototype, "dropHandler", null);
    __decorate([
        HostListener('dragover', ['$event']),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [DragEvent]),
        __metadata("design:returntype", void 0)
    ], UploadxDropDirective.prototype, "onDragOver", null);
    __decorate([
        HostListener('dragleave', ['$event']),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [DragEvent]),
        __metadata("design:returntype", void 0)
    ], UploadxDropDirective.prototype, "onDragLeave", null);
    UploadxDropDirective = __decorate([
        Directive({
            selector: '[uploadxDrop]'
        }),
        __metadata("design:paramtypes", [UploadxService])
    ], UploadxDropDirective);
    return UploadxDropDirective;
}());

var UploadxModule = /** @class */ (function () {
    function UploadxModule() {
    }
    UploadxModule = __decorate([
        NgModule({
            declarations: [UploadxDirective, UploadxDropDirective],
            exports: [UploadxDirective, UploadxDropDirective]
        })
    ], UploadxModule);
    return UploadxModule;
}());

/**
 * Generated bundle index. Do not edit.
 */

export { ErrorHandler, Uploader, UploaderX, UploadxModule, UploadxService, UploadxDirective as ɵa, UploadxDropDirective as ɵb };
//# sourceMappingURL=laravel-ngx-uploadx.js.map
